import React from 'react';
import styles from './rtbCampaignSummaryStep.module.scss';
import {
  RtbCampaignSummaryStepProps,
  RtbCampaignSummaryStepState,
  DeliveryTargetModalType
} from './RtbCampaignSummaryStepModel';
import { Button, Tab, Tabs, Tooltip } from 'react-bootstrap';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { TAInfoCard } from 'components/TAInfoCard/TAInfoCard';
import _ from 'lodash';
import { alertMessage } from 'components/AlertDialog';
import { withOverlayHoverTip } from 'hoc/withOverlayHoverTip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { LegacyEstimateViewModelData } from 'core/rtbCampaign/RtbCampaignManager';
import {
  FlowMediaSummarySection,
  FlowSummarySection
} from 'components/FlowSummarySection/FlowSummarySection';
import { CreativeSummaryStepState } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/CreativeSummaryStepModel';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { Modal } from 'components/common/Modal/Modal';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  DeliveryTarget,
  FilterReason,
  SpaceDeliverabilityData
} from 'core/rtbCampaign/RtbCampaign';

const DELIVERY_TARGET_DISPLAY_LIMIT = 20;

enum CampaignSummaryTab {
  CAMPAIGN,
  CREATIVE
}

export class RtbCampaignSummaryStep extends React.Component<
  RtbCampaignSummaryStepProps,
  RtbCampaignSummaryStepState
> {
  handler?: number;

  initModel() {
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
    this.props.model.initCreativeSummaryModel();
    this.props.model.getLegacyEstimateData();
    this.props.model.getSpaceDeliverabilityData();
  }

  componentDidMount() {
    this.initModel();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.model === this.props.model) {
      return;
    }

    prevProps.model.unmount(this.handler);
    this.initModel();
  }

  componentWillUnmount() {
    this.props.model.unmount(this.handler);
  }

  renderLegacyEstimateData() {
    const data = this.props.model.state.legacyEstimateData;
    const onShowAddSaveTargetingModal =
      this.props.model.onShowAddSaveTargetingModal;
    return (
      <div className={styles.estimateData}>
        {data ? (
          <>
            {this.renderLegacyEstimateDataOfField('uniqueUser', data)}
            {this.renderLegacyEstimateDataOfField('impression', data)}
            {data.involvedLimitatinoTypes.length > 0 && (
              <div className={styles.estimateLimitation}>
                <div className={styles.estimateLimitationDesc}>
                  {i18n.t('campaignSummary.labels.estimateDesc')}
                </div>
                {this.renderLegacyEstimateLimitations(
                  data.involvedLimitatinoTypes
                )}
              </div>
            )}
          </>
        ) : (
          <div className={styles.estimateLimitationDesc}>
            {this.props.model.noLegacyEstimateDes}
          </div>
        )}
        <>
          <div className={styles.saveHint}>
            {i18n.t('taInfoCard.labels.saveTargeting')}
          </div>
          <button
            className={'btn btn-secondary btn-sm'}
            onClick={_.partial(onShowAddSaveTargetingModal, true)}
          >
            {i18n.t('taInfoCard.buttons.saveTargeting')}
          </button>
        </>
      </div>
    );
  }

  renderLegacyEstimateDataOfField(
    field: string,
    legacyEstimateData: LegacyEstimateViewModelData
  ) {
    const InformationButtion = withOverlayHoverTip(
      FontAwesomeIcon,
      <Tooltip id={field}>
        {i18n.t(`campaignSummary.labels.${field}Information`)}
      </Tooltip>
    );
    const data = legacyEstimateData[field];
    return (
      <div className={styles.estimateDataField}>
        <div className={styles.estimateDataTitle}>
          {i18n.t(`campaignSummary.labels.${field}Title`)}
          <InformationButtion icon={faInfoCircle} placement='right' />
        </div>
        <div className={styles.estimateDataContent}>
          {data >= 50 ? (
            data
          ) : (
            <div className={styles.estimateLowHint}>
              <div>{'<50'}</div>
              <div>{i18n.t('campaignSummary.labels.estimateLowHint')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderLegacyEstimateLimitations(involvedLimitatinoTypes: string[]) {
    return (
      <ul>
        {involvedLimitatinoTypes.map((type) => (
          <li key={type}>{i18n.t(`limitation.labels.${type}`)}</li>
        ))}
      </ul>
    );
  }

  renderDeliveryTargetList(
    deliveryTargets: {
      deliveryTargetValue: string;
      deliveryTargetName: string;
    }[],
    type: DeliveryTargetModalType
  ) {
    if (deliveryTargets.length === 0) {
      return (
        <div className={styles.spaceDeliverabilityList}>
          {i18n.t('common.labels.noData')}
        </div>
      );
    }
    const truncated = deliveryTargets.slice(0, DELIVERY_TARGET_DISPLAY_LIMIT);
    const hasMore = deliveryTargets.length > DELIVERY_TARGET_DISPLAY_LIMIT;
    return (
      <>
        <div className={styles.spaceDeliverabilityList}>
          {truncated.map((target) => target.deliveryTargetName).join(', ')}
        </div>
        {hasMore && (
          <div
            className={styles.showMoreLink}
            onClick={_.partial(this.props.model.openDeliveryTargetModal, type)}
          >
            {i18n.t('common.labels.showMore')}
          </div>
        )}
      </>
    );
  }

  getTargetsByModalType(
    data: SpaceDeliverabilityData,
    type: DeliveryTargetModalType
  ): DeliveryTarget[] {
    // 後端目前會把每個 FilterReason 的 key 都補成空陣列（SpaceFilterService 的
    // 「Pre-fill every FilterReason key」），但型別上是 Partial —— 留著 fallback 是為了
    // 不讓「後端哪天不補了」變成 undefined.length 的執行時錯誤。
    const byReason = (reason: FilterReason) =>
      data.nonDeliverableByReason[reason] ?? [];
    switch (type) {
      case 'deliverable':
        return data.deliverable;
      case 'weatherNonDeliverable':
        return byReason(FilterReason.WEATHER);
      case 'changeNonDeliverable':
        // 已刪除併進門市異動，與活動列表的統計卡同一個口徑。
        return [
          ...byReason(FilterReason.SPACE_DELETED),
          ...byReason(FilterReason.DELIVERY_TARGET_CHANGE),
          ...byReason(FilterReason.SIMULCAST_MISMATCH)
        ];
      case 'overBindingLimitNonDeliverable':
        return byReason(FilterReason.STORE_OVER_BINDING_LIMIT);
    }
  }

  renderDeliveryTargetInfoModal() {
    const deliveryUnit = this.props.model.deliveryUnit;
    const { deliveryTargetModalType, spaceDeliverabilityData } =
      this.props.model.state;
    if (!deliveryTargetModalType || !spaceDeliverabilityData) {
      return null;
    }
    // non-DEVICE (including undefined) falls back to STORE; lowerCase -> device/store
    const i18nPrefix = _.lowerCase(deliveryUnit ?? DeliveryUnit.STORE);
    const deliveryTargets = this.getTargetsByModalType(
      spaceDeliverabilityData,
      deliveryTargetModalType
    );
    const title = i18n.t(
      `campaignSummary.labels.${i18nPrefix}${_.upperFirst(deliveryTargetModalType)}`
    );
    return (
      <Modal
        title={title}
        dismiss={this.props.model.closeDeliveryTargetModal}
        secondaryButton={{
          title: i18n.t('common.buttons.close'),
          callback: this.props.model.closeDeliveryTargetModal
        }}
      >
        <div className={styles.storeModalContent}>
          {deliveryTargets.map((target, index) => (
            <div key={index} className={styles.storeModalItem}>
              {target.deliveryTargetName}
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  renderSpaceDeliverabilityData() {
    const data = this.props.model.state.spaceDeliverabilityData;
    if (!data) {
      return null;
    }
    // non-DEVICE (including undefined) falls back to STORE; lowerCase -> device/store
    const i18nPrefix = _.lowerCase(
      this.props.model.deliveryUnit ?? DeliveryUnit.STORE
    );
    const nonDeliverableTotal = Object.values(
      data.nonDeliverableByReason
    ).reduce((sum, list) => sum + (list?.length ?? 0), 0);
    const totalCount = data.deliverable.length + nonDeliverableTotal;
    // 空的區塊會顯示「無」而不是收起來，所以「門市超過鎖定上限」只在真的有機台被擋下
    // 時才列 —— 否則每一檔戶外活動的摘要頁都會多一行永遠是「無」的區塊。
    //
    // ⚠️ 判「陣列非空」而不是「key 在不在」：後端刻意把每個 FilterReason 的 key 都補成
    // 空陣列（SpaceFilterService「Pre-fill every FilterReason key」那段），讓 JSON 形狀
    // 穩定，所以 key 一定存在。用 key 判斷等於永遠成立。
    //
    // 判資料而不是自己再判一次渠道是不是電子紙，是為了不讓前端多養一份「哪些渠道有名額
    // 限制」的知識（ECOPD-5621）。
    const sectionTypes: DeliveryTargetModalType[] = [
      'deliverable',
      'weatherNonDeliverable',
      'changeNonDeliverable'
    ];
    if (
      this.getTargetsByModalType(data, 'overBindingLimitNonDeliverable')
        .length > 0
    ) {
      sectionTypes.push('overBindingLimitNonDeliverable');
    }
    const sections: {
      type: DeliveryTargetModalType;
      targets: DeliveryTarget[];
    }[] = sectionTypes.map((type) => ({
      type,
      targets: this.getTargetsByModalType(data, type)
    }));
    return (
      <div className={styles.spaceDeliverability}>
        <div className={styles.spaceDeliverabilityHeader}>
          {i18n.t(`campaignSummary.titles.${i18nPrefix}Deliverability`)}
          {': '}
          {data.deliverable.length}
          {' / '}
          {totalCount}
        </div>
        {sections.map(({ type, targets }) => (
          <div key={type} className={styles.spaceDeliverabilitySection}>
            <div className={styles.spaceDeliverabilityTitle}>
              {i18n.t(
                `campaignSummary.labels.${i18nPrefix}${_.upperFirst(type)}`
              )}
            </div>
            {this.renderDeliveryTargetList(targets, type)}
          </div>
        ))}
      </div>
    );
  }

  renderNormalCampaignSummary() {
    const model = this.props.model;
    const estimatedAudience = model.estimatedAudience;
    const draftBindingSummary = model.getCreativeBindingSummaryData();
    const renderEstimatedAudience = () => (
      <TAInfoCard
        orderType={model.order.orderType}
        channel={_.get(model.l1Object, 'channel', ChannelGroupSetting.RTB.key)}
        advertiserId={model.order.advertiserId}
        appliedSavedTAInfo={model.appliedSavedTAInfo}
        targetingValue={model.targetingValue}
        estimatedAudience={estimatedAudience}
        audienceLowestThreshold={model.audienceLowestThreshold}
        savedTargetingList={model.savedTargetingList}
        showAddSaveTargetingModal={model.state.showAddSaveTargetingModal}
        onShowAddSaveTargetingModal={model.onShowAddSaveTargetingModal}
      />
    );
    const limitationsSummaryData = model.getLimitationsSummaryData();
    const trackingSummaryData = model.getTrackingSummaryData();
    return (
      <>
        <FlowSummarySection {...model.getAdTypeSummaryData()} />
        <div className={styles.sepLine} />
        <div className={styles.basicSummary}>
          <FlowSummarySection {...model.getSetUpSummaryData()} />
          {model.showLegacyEstimation && this.renderLegacyEstimateData()}
          {model.showSpaceDeliverability &&
            this.renderSpaceDeliverabilityData()}
        </div>
        {!_.isEmpty(limitationsSummaryData.summaryData) && (
          <>
            <div className={styles.sepLine} />
            <FlowSummarySection
              {...model.getLimitationsSummaryData()}
              renderOtherInfo={renderEstimatedAudience}
            />
          </>
        )}
        {model.flowModel.needSetupTracking &&
          trackingSummaryData &&
          !_.isEmpty(trackingSummaryData.summaryData) && (
            <>
              <div className={styles.sepLine} />
              <FlowSummarySection {...trackingSummaryData} />
            </>
          )}
        {draftBindingSummary && !_.isEmpty(draftBindingSummary.summaryData) && (
          <>
            <div className={styles.sepLine} />
            <FlowSummarySection {...draftBindingSummary} />
          </>
        )}
      </>
    );
  }

  renderCampaignSummary() {
    const model = this.props.model;
    const pmpSummaryData = model.getPmpSummaryData();
    return (
      <>
        <div className={styles.title}>
          {i18n.t('campaignSummary.titles.checkYourSettings')}
        </div>
        <div className={styles.content}>
          {pmpSummaryData ? (
            // RKB-2712：把**既有**的門市投遞資訊板塊
            // 掛到 PMP 分支旁邊。必須包在 `.basicSummary` 裡 —— 板塊的樣式是巢狀在它
            // 底下的（rtbCampaignSummaryStep.module.scss 的 .basicSummary → .spaceDeliverability），
            // 不包這層板塊會失去全部樣式；而該容器的 flex 排版正是一般活動那條路的既有結構。
            <div className={styles.basicSummary}>
              <FlowSummarySection {...pmpSummaryData} />
              {model.showSpaceDeliverability &&
                this.renderSpaceDeliverabilityData()}
            </div>
          ) : (
            this.renderNormalCampaignSummary()
          )}
        </div>
      </>
    );
  }

  renderCreativeSummary(creativeSummaryStepState: CreativeSummaryStepState) {
    const { basicSummaryData, mediaSummaryData } = creativeSummaryStepState;

    return (
      <>
        <div className={styles.title}>
          {i18n.t('campaignSummary.titles.checkYourSettings')}
        </div>
        <div className={styles.content}>
          {basicSummaryData && <FlowSummarySection {...basicSummaryData} />}
          {mediaSummaryData && (
            <FlowMediaSummarySection {...mediaSummaryData} />
          )}
        </div>
      </>
    );
  }

  onTabSelect = (tab: string | null) => {
    if (tab === null) {
      return;
    }
    this.props.model.goSubStep(+tab);
  };

  renderCloseButton() {
    return (
      <Button
        variant='secondary'
        size='sm'
        onClick={this.props.model.redirectToLastPage}
      >
        {i18n.t('common.buttons.close')}
      </Button>
    );
  }

  renderSetupButtonSet() {
    const model = this.props.model;
    const objectType = model.flowModel.objectType;
    const onSaveDraft = model.flowModel.onSaveDraft;
    const hasCreative = !!model.creativeSummaryStepModel;
    const errors = model.flowModel.isPmp ? model.getPmpErrors() : undefined;
    const hasError = !_.isEmpty(errors);
    return (
      <>
        {hasCreative ? (
          model.subStepIndex === CampaignSummaryTab.CREATIVE ? (
            <Button
              disabled={hasError}
              variant='primary'
              style={{ marginRight: '20px' }}
              size='sm'
              onClick={model.submit}
            >
              {i18n.t('common.buttons.submit')}
            </Button>
          ) : (
            <Button
              disabled={hasError}
              variant='primary'
              style={{ marginRight: '20px' }}
              size='sm'
              onClick={_.partial(model.goSubStep, CampaignSummaryTab.CREATIVE)}
            >
              {i18n.t('common.buttons.nextPage')}
            </Button>
          )
        ) : (
          <>
            <Button variant='primary' size='sm' onClick={model.submit}>
              {objectType === 'campaign'
                ? i18n.t('common.buttons.submit')
                : i18n.t('common.buttons.publish')}
            </Button>
            {onSaveDraft && (
              <Button
                variant='secondary'
                size='sm'
                onClick={_.partial(onSaveDraft, model.campaign)}
              >
                {i18n.t('campaign.buttons.saveDraft')}
              </Button>
            )}
          </>
        )}
        <Button variant='secondary' size='sm' onClick={model.goLast}>
          {i18n.t('campaign.buttons.back')}
        </Button>
      </>
    );
  }

  render() {
    const model = this.props.model;
    const hasCreative = !!model.creativeSummaryStepModel;
    return (
      <div className={styles.container}>
        {model.state.loading && <LoadingIndicator />}
        {this.renderDeliveryTargetInfoModal()}
        {model.state.showPublishBindingFailed &&
          alertMessage(
            i18n.t('common.warning'),
            i18n.t('campaignSetupFlow.messages.publishBindingFailed'),
            model.redirectToLastPage
          )}
        {hasCreative ? (
          <Tabs
            id='campaign-summary-tab'
            activeKey={model.subStepIndex}
            onSelect={this.onTabSelect}
          >
            <Tab
              eventKey={CampaignSummaryTab.CAMPAIGN}
              title={i18n.t('stepSideBar.labels.campaignSettingSummary')}
            >
              {this.renderCampaignSummary()}
            </Tab>
            {model.state.creativeSummaryStepState && (
              <Tab
                eventKey={CampaignSummaryTab.CREATIVE}
                title={i18n.t('stepSideBar.labels.creativeSettingSummary')}
              >
                {this.renderCreativeSummary(
                  model.state.creativeSummaryStepState
                )}
              </Tab>
            )}
          </Tabs>
        ) : (
          this.renderCampaignSummary()
        )}
        <div className={styles.buttonArea}>
          {model.flowModel.type === 'summary'
            ? this.renderCloseButton()
            : this.renderSetupButtonSet()}
        </div>
      </div>
    );
  }
}
